import { patientDisplayName } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

export type AllergyDict = {
  clinicalStatus: Record<string, string>;
  criticality: Record<string, string>;
  category: Record<string, string>;
};

function formatDate(dateString?: string): string {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString("pt-BR");
  } catch {
    return dateString;
  }
}

function allergenKey(allergy: fhir5.AllergyIntolerance): { code: string; display: string } {
  let code = "unknown";
  let display = "Alérgeno não especificado";
  if (allergy.code?.coding?.[0]) {
    code = allergy.code.coding[0].code || "unknown";
    display =
      allergy.code.coding[0].display ||
      allergy.code.text ||
      "Alérgeno não especificado";
  } else if (allergy.code?.text) {
    display = allergy.code.text;
    code = allergy.code.text;
  }
  return { code, display };
}

function clinicalStatusCode(allergy: fhir5.AllergyIntolerance): string {
  if (typeof allergy.clinicalStatus === "string") return allergy.clinicalStatus;
  return allergy.clinicalStatus?.coding?.[0]?.code || "unknown";
}

export function buildAllergyInsightCharts(
  allergies: fhir5.AllergyIntolerance[],
  dict: AllergyDict
) {
  const clinicalMap = new Map<string, number>();
  const criticalityMap = new Map<string, number>();
  const allergenMap = new Map<string, number>();

  for (const allergy of allergies) {
    const clinical = clinicalStatusCode(allergy);
    clinicalMap.set(clinical, (clinicalMap.get(clinical) || 0) + 1);

    const criticality = allergy.criticality || "unknown";
    criticalityMap.set(criticality, (criticalityMap.get(criticality) || 0) + 1);

    const { display } = allergenKey(allergy);
    allergenMap.set(display, (allergenMap.get(display) || 0) + 1);
  }

  const statusItems: InsightChartItem[] = Array.from(clinicalMap.entries())
    .map(([key, value]) => ({
      key,
      label: dict.clinicalStatus[key] || key,
      value,
    }))
    .sort((a, b) => b.value - a.value);

  const secondaryItems: InsightChartItem[] = Array.from(criticalityMap.entries())
    .map(([key, value]) => ({
      key,
      label: dict.criticality[key] || key,
      value,
    }))
    .sort((a, b) => b.value - a.value);

  const rankingItems: InsightChartItem[] = Array.from(allergenMap.entries())
    .map(([key, value]) => ({ key, label: key, value }))
    .sort((a, b) => b.value - a.value);

  return {
    statusItems,
    secondaryItems,
    rankingItems,
    total: allergies.length,
  };
}

export function buildAllergyPatientRows(
  allergies: fhir5.AllergyIntolerance[],
  patients: fhir5.Patient[],
  dict: AllergyDict
): InsightPatientRow[] {
  const patientMap = new Map<string, fhir5.Patient>();
  for (const patient of patients) {
    if (patient.id) patientMap.set(patient.id, patient);
  }

  const groups = new Map<string, InsightPatientRow>();
  for (const allergy of allergies) {
    const patientId = allergy.patient?.reference?.replace("Patient/", "") || "";
    if (!patientId) continue;

    const { code, display } = allergenKey(allergy);
    const statusKey = clinicalStatusCode(allergy);
    const criticalityKey = allergy.criticality || "unknown";

    if (!groups.has(patientId)) {
      groups.set(patientId, {
        patientId,
        patientName: patientDisplayName(patientMap.get(patientId)),
        patient: patientMap.get(patientId),
        items: [],
      });
    }
    groups.get(patientId)!.items.push({
      itemKey: display,
      label: display,
      statusKey,
      statusLabel: dict.clinicalStatus[statusKey] || statusKey,
      secondaryKey: criticalityKey,
      practitioner: "Não informado",
      date: formatDate(allergy.recordedDate),
    });
  }

  return Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
}
